import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, FileText, Paperclip } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { adminInvoicesService } from '../../services/admin.invoices.service'
import type {
  MemberTaxProfileDocument,
  ProviderInvoiceDocument,
  ProviderInvoiceLineItem,
  ProviderInvoiceLineType,
} from '@/types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoice: (ProviderInvoiceDocument & { id: string }) | null
  providerName: string
  onActionComplete: () => void
  onApprove: (id: string) => Promise<void>
  onReject: (id: string, note: string) => Promise<void>
  onMarkPaid: (id: string) => Promise<void>
  onEditLineItems: (id: string, lineItems: ProviderInvoiceLineItem[]) => Promise<void>
}

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(url)
}

function tsToDate(ts: ProviderInvoiceDocument['submittedAt']): string {
  if (!ts) return '—'
  return ts.toDate().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function lineItemsEqual(a: ProviderInvoiceLineItem[], b: ProviderInvoiceLineItem[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!
    const y = b[i]!
    if (
      x.type !== y.type ||
      x.description !== y.description ||
      Number(x.amount) !== Number(y.amount) ||
      (x.receiptUrl ?? '') !== (y.receiptUrl ?? '')
    ) {
      return false
    }
  }
  return true
}

export function ReviewInvoiceDrawer({
  open,
  onOpenChange,
  invoice,
  providerName,
  onActionComplete,
  onApprove,
  onReject,
  onMarkPaid,
  onEditLineItems,
}: Props) {
  const [taxProfile, setTaxProfile] = useState<MemberTaxProfileDocument | null>(null)
  const [taxLoading, setTaxLoading] = useState(false)
  const [draftItems, setDraftItems] = useState<ProviderInvoiceLineItem[]>([])
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectNote, setRejectNote] = useState('')
  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !invoice) return
    setDraftItems(invoice.lineItems.map((li) => ({ ...li })))
    setActionError(null)
    setTaxProfile(null)
    setTaxLoading(true)
    adminInvoicesService
      .getMemberTaxProfile(invoice.memberId)
      .then(setTaxProfile)
      .catch(() => setTaxProfile(null))
      .finally(() => setTaxLoading(false))
  }, [open, invoice])

  // The public profile is informational only. Approval is authorized by the
  // server after it verifies memberTaxProfiles/{memberId}/secure/taxId.
  const hasTaxProfile = !!taxProfile
  const itemsDirty = useMemo(
    () => !!invoice && !lineItemsEqual(draftItems, invoice.lineItems),
    [draftItems, invoice],
  )
  const draftSubtotal = useMemo(
    () => draftItems.reduce((acc, li) => acc + (Number(li.amount) || 0), 0),
    [draftItems],
  )

  if (!invoice) return null

  function updateItem(index: number, patch: Partial<ProviderInvoiceLineItem>) {
    setDraftItems((items) => items.map((li, i) => (i === index ? { ...li, ...patch } : li)))
  }

  async function withBusy<T>(fn: () => Promise<T>): Promise<T | null> {
    setBusy(true)
    setActionError(null)
    try {
      return await fn()
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Action failed.'))
      return null
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveEdits() {
    if (!invoice) return
    const inv = invoice
    const ok = await withBusy(async () => {
      await onEditLineItems(inv.id, draftItems)
      return true
    })
    if (ok) onActionComplete()
  }

  async function handleApprove() {
    if (!invoice) return
    const inv = invoice
    const ok = await withBusy(async () => {
      await onApprove(inv.id)
      return true
    })
    if (ok) onActionComplete()
  }

  async function handleConfirmReject() {
    if (!invoice || !rejectNote.trim()) return
    const inv = invoice
    const ok = await withBusy(async () => {
      await onReject(inv.id, rejectNote.trim())
      return true
    })
    if (ok) {
      setRejectOpen(false)
      setRejectNote('')
      onActionComplete()
    }
  }

  async function handleConfirmMarkPaid() {
    if (!invoice) return
    const inv = invoice
    const ok = await withBusy(async () => {
      await onMarkPaid(inv.id)
      return true
    })
    if (ok) {
      setMarkPaidOpen(false)
      onActionComplete()
    }
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader className="space-y-2">
            <SheetTitle className="flex items-center gap-3">
              <span>{providerName || invoice.memberId}</span>
              {taxLoading ? (
                <Badge variant="secondary">Checking W-9…</Badge>
              ) : hasTaxProfile ? (
                <Badge className="bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  W-9 details available
                </Badge>
              ) : (
                <Badge variant="secondary">W-9 verified on approval</Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              {invoice.productionName} · Submitted {tsToDate(invoice.submittedAt)} · Invoice date{' '}
              {invoice.invoiceDate}
            </SheetDescription>
            {taxProfile?.signedDate && (
              <p className="text-xs text-muted-foreground">
                W-9 signed {taxProfile.signedDate.toDate().toLocaleDateString()}
              </p>
            )}
          </SheetHeader>

          <div className="space-y-6 px-4 pb-6">
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Line Items</h3>
                <p className="text-sm text-muted-foreground">
                  Subtotal: <span className="font-semibold">${draftSubtotal.toLocaleString()}</span>
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-28">Amount</TableHead>
                    <TableHead className="w-20 text-right">Receipt</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draftItems.map((li, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select
                          value={li.type}
                          onValueChange={(v) =>
                            updateItem(i, { type: v as ProviderInvoiceLineType })
                          }
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="labor">Labor</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={li.description}
                          onChange={(e) => updateItem(i, { description: e.target.value })}
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={li.amount}
                          onChange={(e) =>
                            updateItem(i, { amount: Number(e.target.value) || 0 })
                          }
                          className="h-8"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        {li.receiptUrl ? (
                          <button
                            type="button"
                            onClick={() => setReceiptPreview(li.receiptUrl ?? null)}
                            className="text-primary hover:underline inline-flex items-center"
                          >
                            {isImageUrl(li.receiptUrl) ? (
                              <img
                                src={li.receiptUrl}
                                alt="receipt"
                                className="h-8 w-8 object-cover rounded border"
                              />
                            ) : (
                              <Paperclip className="h-4 w-4" />
                            )}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>

            {invoice.notes && (
              <section className="space-y-1">
                <h3 className="text-sm font-semibold">Notes from provider</h3>
                <p className="text-sm italic text-muted-foreground whitespace-pre-wrap">
                  {invoice.notes}
                </p>
              </section>
            )}

            {invoice.rejectionNote && (
              <section className="space-y-1">
                <h3 className="text-sm font-semibold text-destructive">Rejection note</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {invoice.rejectionNote}
                </p>
              </section>
            )}

            {actionError && (
              <Alert variant="destructive">
                <AlertDescription>{actionError}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
              {itemsDirty && (
                <Button variant="outline" disabled={busy} onClick={() => void handleSaveEdits()}>
                  Save Edits
                </Button>
              )}

              {invoice.status === 'submitted' && (
                <>
                  <Button
                    variant="destructive"
                    disabled={busy}
                    onClick={() => setRejectOpen(true)}
                  >
                    Reject
                  </Button>
                  <Button disabled={busy} onClick={() => void handleApprove()}>
                    Approve
                  </Button>
                </>
              )}

              {invoice.status === 'approved' && (
                <Button disabled={busy} onClick={() => setMarkPaidOpen(true)}>
                  Mark Paid
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Reject confirmation */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="reject-note">
              Reason (sent to provider) <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="reject-note"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={4}
              placeholder="Briefly explain what needs to be fixed…"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy || !rejectNote.trim()}
              onClick={() => void handleConfirmReject()}
            >
              {busy ? 'Rejecting…' : 'Reject Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid confirmation */}
      <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirm that you have paid this invoice through the approved payment process.
            This action marks the invoice paid and notifies the provider.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={() => void handleConfirmMarkPaid()}>
              {busy ? 'Saving…' : 'Confirm Paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt preview */}
      <Dialog
        open={!!receiptPreview}
        onOpenChange={(o) => !o && setReceiptPreview(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Receipt
            </DialogTitle>
          </DialogHeader>
          {receiptPreview && (
            isImageUrl(receiptPreview) ? (
              <img
                src={receiptPreview}
                alt="Receipt preview"
                className="max-h-[70vh] w-full object-contain"
              />
            ) : (
              <iframe
                src={receiptPreview}
                title="Receipt preview"
                className="w-full h-[70vh]"
              />
            )
          )}
          <DialogFooter>
            <Button variant="outline" asChild>
              <a href={receiptPreview ?? '#'} target="_blank" rel="noreferrer noopener">
                Open in new tab
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}
